import trackEvent from "components/tracking/trackEvent";
import { usePlacementOnelinkUrl } from "components/common/useAppStoreLink";
import { PlanGateModal } from "components/website/components/planGate";
import stipplLogo from "components/website/img/stippl-logo.svg";
import stipplLogoWhite from "components/website/img/stippl-logo--impact.svg";
import MapSlot from "components/website/partners/shared/MapSlot";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fillUi, flagUrl, GuideLangContext, useGuideUi, useHeading } from "../discoverSections";
import discoverStyles from "../discoverSections.module.scss";
import { CountryNamesContext, reducedMotion, stopsText, useBrowserLayoutEffect, useRouteMapUi, withUnit } from "./copy";
import {
	buildRouteMapData,
	DURATION_GROUPS,
	heroPoint,
	pointOfStop,
	routeImages,
	stopOfPoint,
	type AddSource,
	type DurationGroup,
	type MapPoint,
	type MapRoute,
	type RouteMapData,
	type RouteMapGuide,
	type RouteStopData,
} from "./data";
import { Back, Close, Plus } from "./icons";
import { phoneOnelink, type UtmContent } from "./links";
import { insertAtLeastDistance, keyOfName, MAX_STOPS, routeToStops, totalNights } from "./plannerLogic";
import { openRouteMapTab, resetRouteMapState, ROUTE_MAP_ID, setMyStops, setSelectedRoute, useRouteMapState, type RouteMapTab } from "./routeStore";
import { PanelCta, SecondaryCta, Steps, type Step } from "./PanelParts";
import PlaceSheet from "./PlaceSheet";
import RoutesTab, { GROUP_LABEL } from "./RoutesTab";
import { jobKey, useSaveRoute, type SaveJob } from "./useSaveRoute";
import styles from "./CountryRouteMap.module.scss";
import { useContext } from "react";

/**
 * Pick or build a route on a country hub: the hub's ready-made route cards as one interactive map (Ilse's v29). It takes the place of
 * `RoutesSection` on the hubs in COUNTRY_ROUTE_MAP_COUNTRIES (data.ts) and keeps that section's heading and intro, so the page's
 * SEO is the same; the route list is server-rendered too, and only the map is browser-only (`MapSlot`).
 *
 * The section is a shell: a header (the open route's photo, "Plan your {country} route", the Routes / My route tabs), the stepper, the
 * open tab and the CTA row at the foot, beside the map; on a phone the header, then the map, then the panel. The map shows whatever the
 * open tab is about: the route that is open or previewed, or my route.
 *
 * Until the visitor first does something, the map takes the routes in turn (3.4 s each); a card under the mouse previews its route.
 */

// MapLibre is big and browser-only: its chunk loads when the slot is near the screen, never with the hub.
const RouteMapScene = lazy(() => import("./RouteMapScene"));
// The builder only shows on one hub: its chunk loads when the tab opens, not with all 60 hubs.
const MineTab = lazy(() => import("./MineTab"));

const TABS: RouteMapTab[] = ["routes", "mine"];

const NO_STOPS: RouteStopData[] = [];

/** How long a toast ("Now your route", "Pai added · 2 nights") stays on the map (the toast's animation in the stylesheet is as long) */
const TOAST_MS = 2600;
/** How long each route is on the map before the next, until the visitor first does something */
const ROTATE_MS = 3400;

interface CountryRouteMapProps {
	/** the country as the page says it, localized */
	name: string;
	countryCode: string;
	guide: RouteMapGuide;
}

/** The photo behind the header: two layers that cross-fade, so a new route's photo fades in over the last one once it has loaded. */
const HeaderPhoto = ({ src }: { src: string | null }) => {
	const [layers, setLayers] = useState<[string, string]>([src ?? "", ""]);
	const [on, setOn] = useState<0 | 1>(0);
	const pending = useRef<0 | 1 | null>(null);
	useEffect(() => {
		if (!src || layers[on] === src) return;
		const next = on === 0 ? 1 : 0;
		pending.current = next;
		setLayers((l) => (next === 0 ? [src, l[1]] : [l[0], src]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src]);
	const shown = (i: 0 | 1) => () => {
		if (pending.current === i) {
			pending.current = null;
			setOn(i);
		}
	};
	return (
		<div className={styles.headPhoto} aria-hidden="true">
			{([0, 1] as const).map((i) =>
				layers[i] ? <img key={i} className={on === i ? styles.headPhotoOn : ""} src={layers[i]} alt="" onLoad={shown(i)} decoding="async" /> : null
			)}
		</div>
	);
};

const CountryRouteMap = ({ name, countryCode, guide }: CountryRouteMapProps) => {
	const data = useMemo(() => buildRouteMapData(guide, name), [guide, name]);
	// The strings of the section say the country by name: the provider has to be above everything that reads one
	return (
		<CountryNamesContext.Provider value={data.names}>
			<Section name={name} countryCode={countryCode} countrySlug={guide.slug!} data={data} />
		</CountryNamesContext.Provider>
	);
};

interface SectionProps {
	name: string;
	countryCode: string;
	countrySlug: string;
	data: RouteMapData;
}

const Section = ({ name, countryCode, countrySlug, data }: SectionProps) => {
	const ui = useGuideUi();
	const lang = useContext(GuideLangContext);
	const t = useRouteMapUi();
	// Same heading as RoutesSection: the kicker and H2 are the hub's SEO.
	const heading = useHeading("routes", `Plan your adventure in ${name}`, `Our travel routes through ${name}`, { vars: { name } });
	const state = useRouteMapState(countrySlug);
	const save = useSaveRoute();
	// The OneLink the phone's "open the app" links are built on; each one adds its own `af_adset` (links.ts)
	const onelink = usePlacementOnelinkUrl("countrymap");
	// Where a route saved from a phone is opened: the app, no deep link (it only knows `join/<tripId>`)
	const savedUrl = usePlacementOnelinkUrl("countrymap_saved");
	const [filter, setFilter] = useState<DurationGroup | "all">("all");
	// The "Add a stop" sheet of My route is open: it is the panel's CTA that says "Done" then, so the shell holds it.
	const [adding, setAdding] = useState(false);
	const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
	// The destination whose card is open on the map, and the one under the mouse (a pin, a stop of a card, a row of My route).
	const [place, setPlace] = useState<string | null>(null);
	const [hover, setHover] = useState<string | null>(null);
	// The route on the map in the first view: the one in turn, or the one under the mouse
	const [preview, setPreview] = useState<string | null>(null);
	// The map takes the routes in turn until the visitor first does something
	const [auto, setAuto] = useState(true);
	const autoRef = useRef(true);
	const hoveringRef = useRef(false);
	const turn = useRef(0);
	// The camera frames the open tab again when this changes: a stop leaving My route, or a route copied into it.
	const [framing, setFraming] = useState(0);
	const [folded, setFolded] = useState(false);
	const sheetRef = useRef<HTMLDivElement>(null);
	const pointerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef<HTMLDivElement>(null);
	const [pointerTop, setPointerTop] = useState<number | null>(null);
	// `isPhone` is false on the server and in the first client render, then set after mount.
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	const tab: RouteMapTab = state.tab;
	const phone = mounted && save.isPhone;

	const groups = DURATION_GROUPS.filter((g) => data.routes.some((r) => r.group === g));
	const routes = filter === "all" ? data.routes : data.routes.filter((r) => r.group === filter);
	const selected = data.routes.find((r) => r.id === state.selectedRouteId) ?? null;
	const inRoute = useMemo(() => new Set(state.stops.map((s) => s.key)), [state.stops]);
	const images = useMemo(() => routeImages(data.routes, data.points), [data]);
	const hero = useMemo(() => heroPoint(data.points), [data.points]);
	const overview = tab === "routes" && !selected;
	const previewRoute = overview ? (data.routes.find((r) => r.id === preview) ?? null) : null;

	// A failed save is about the tab it happened on, and so is an open sheet.
	const resetSave = save.reset;
	useEffect(() => resetSave(), [tab, resetSave]);
	useEffect(() => setAdding(false), [tab]);
	// My route is empty on every visit: the section leaving the page clears it
	useEffect(() => () => resetRouteMapState(countrySlug), [countrySlug]);
	useEffect(() => {
		if (!toast) return undefined;
		const timer = setTimeout(() => setToast(null), TOAST_MS);
		return () => clearTimeout(timer);
	}, [toast]);
	useEffect(() => {
		if (!place) return undefined;
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPlace(null);
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [place]);

	// ---- the rotation: every route in turn on the map, until the visitor first presses, types or scrolls in the section ----
	const turnIds = routes.map((r) => r.id).join(",");
	useEffect(() => {
		if (!mounted || !auto || !overview || place || !turnIds || reducedMotion()) return undefined;
		const ids = turnIds.split(",");
		const next = () => setPreview(ids[turn.current++ % ids.length]);
		next();
		const timer = setInterval(() => {
			if (hoveringRef.current || document.hidden) return;
			next();
		}, ROTATE_MS);
		return () => clearInterval(timer);
	}, [mounted, auto, overview, place, turnIds]);
	const stopAuto = (e: { target: EventTarget | null }) => {
		if (!autoRef.current) return;
		autoRef.current = false;
		setAuto(false);
		// the route in turn goes, unless the press was on a card (its route is what the visitor is after)
		if (!(e.target as HTMLElement | null)?.closest?.("[data-route]")) setTimeout(() => !hoveringRef.current && setPreview(null), 0);
	};
	const onPreview = (id: string | null) => {
		hoveringRef.current = !!id;
		if (id) setPreview(id);
		else if (!autoRef.current) setPreview(null);
	};

	const say = (text: string) => setToast((x) => ({ id: (x?.id ?? 0) + 1, text }));
	const switchTab = (next: RouteMapTab) => {
		if (next === tab) return;
		setPlace(null);
		openRouteMapTab(next);
		trackEvent("countrymap_tab", { tab: next }, "discover");
	};
	const selectPlace = (slug: string) => {
		if (!data.points.some((p) => p.slug === slug)) return;
		setPlace(slug);
		trackEvent("countrymap_place", { slug }, "discover");
	};
	const select = (route: MapRoute) => {
		save.reset();
		setPlace(null);
		setPreview(null);
		setSelectedRoute(route.id);
		trackEvent("countrymap_route", { route_id: route.id, nights: route.nights }, "discover");
	};
	const allRoutes = () => {
		save.reset();
		setPlace(null);
		setSelectedRoute(null);
		hoveringRef.current = false;
	};
	const filterBy = (next: DurationGroup | "all") => {
		setFilter(next);
		turn.current = 0;
		setPreview(null);
	};
	const scratch = () => {
		trackEvent("countrymap_scratch", {}, "discover");
		switchTab("mine");
	};
	// Copies the route into My route and opens it there; it stays the route's own, for saving, until the first edit.
	const customise = (route: MapRoute) => {
		trackEvent("countrymap_customise", { route_id: route.id }, "discover");
		setMyStops(
			routeToStops(route.stops, (s) => pointOfStop(s, data.points)?.slug ?? keyOfName(s.name)),
			route.id
		);
		setPlace(null);
		setFraming((n) => n + 1);
		openRouteMapTab("mine");
		say(t("toastMine", "Now your route"));
	};

	// A new stop goes where it adds the least distance, and the toast says it. The sheet, the tiles, the "Near" chips and the map's place card all add through here.
	const addPoint = (point: MapPoint, source: AddSource) => {
		if (state.stops.length >= MAX_STOPS || state.stops.some((s) => s.key === point.slug)) return;
		trackEvent("countrymap_add", { slug: point.slug, source }, "discover");
		setMyStops(insertAtLeastDistance(state.stops, stopOfPoint(point, data.country)));
		say(t("toastAdded", "{name} added · {n}", { name: point.name, n: withUnit(t, point.nights, "night", "nights") }));
	};
	// The card's button: a destination that is not in my route is added (and My route opens, the card stays), one that is comes out.
	const togglePlace = (point: MapPoint) => {
		if (inRoute.has(point.slug)) {
			setMyStops(state.stops.filter((s) => s.key !== point.slug));
			return;
		}
		addPoint(point, "map");
		if (tab !== "mine") openRouteMapTab("mine");
	};

	// ---- the CTAs. Saving a route signs up in the page, a phone too (the modal, and the trip is made in the new account); a desktop then opens the trip,
	// a phone says it is saved and links to the app. "Plan {country} in Stippl" and "Start from scratch" save nothing: a phone's go to the app's OneLink. ----
	const track = (content: UtmContent, extra: Record<string, string | number> = {}) => trackEvent("countrymap_cta", { content, ...extra }, "discover");
	const saveRoute = (route: MapRoute) => {
		track("cta_route", { route_id: route.id });
		void save.request({ kind: "route", routeId: route.id, name: route.title, nights: route.nights, startDate: state.startDate });
	};
	// My route as it was copied is the ready-made route: saved as that, with its own trip behind it.
	const mineJob = (): SaveJob => {
		const source = state.sourceRouteId ? data.routes.find((r) => r.id === state.sourceRouteId) : undefined;
		return source
			? { kind: "route", routeId: source.id, name: source.title, nights: source.nights, startDate: state.startDate, from: "mine" }
			: { kind: "custom", stops: state.stops, name: t("tripName", "My {country} route"), startDate: state.startDate };
	};
	const saveMine = () => {
		track("cta_myroute", { stops: state.stops.length, nights: totalNights(state.stops) });
		void save.request(mineJob());
	};
	// Nothing to save: "Plan {country} in Stippl" and "Start from scratch" go to the app, through a signup when the visitor has no account.
	const startFresh = (content: "cta_sticky" | "cta_scratch") => {
		track(content);
		void save.startFresh();
	};
	const working = save.status === "working";
	const myNights = totalNights(state.stops);
	const tabSub: Record<RouteMapTab, string> = {
		routes: t("tabRoutesSub", "{n} ready-made", { n: data.routes.length }),
		mine: state.stops.length ? `${stopsText(t, state.stops.length)} · ${withUnit(t, myNights, "night", "nights")}` : t("tabMineEmpty", "build your own"),
	};
	const step: Step = tab === "mine" ? (working ? 3 : 2) : 1;

	/** What the panel's CTA says and does: it follows the tab, the open route, my route and the add sheet. `saves`: a failed save is told next to it. `saved`: a phone's route is in the account, the link opens the app. */
	const cta = ((): { label: string; saves?: boolean; saved?: boolean; dark?: boolean; href?: string; onClick?: () => void } => {
		// A route saved from a phone is saved: the CTA is the way into the app, not a second save.
		const openSaved = { label: t("openSaved", "Open it in the Stippl app"), saved: true, href: savedUrl };
		if (tab === "mine") {
			if (adding) return { label: t("doneBack", "Done · back to my route"), dark: true, onClick: () => setAdding(false) };
			if (!state.stops.length) {
				return phone
					? { label: t("ctaScratch", "Start from scratch in Stippl"), href: phoneOnelink(onelink, "cta_scratch"), onClick: () => track("cta_scratch") }
					: { label: t("ctaScratch", "Start from scratch in Stippl"), onClick: () => startFresh("cta_scratch") };
			}
			const label = myNights === 1 ? t("ctaMineOne", "Save my 1-night route to Stippl") : t("ctaMine", "Save my {n}-night route to Stippl", { n: myNights });
			return save.saved.has(jobKey(mineJob())) ? openSaved : { label, saves: true, onClick: saveMine };
		}
		if (!selected) {
			const label = t("ctaPlan", "Plan {country} in Stippl");
			return phone
				? { label, href: phoneOnelink(onelink, "cta_sticky"), onClick: () => track("cta_sticky") }
				: { label, onClick: () => startFresh("cta_sticky") };
		}
		return save.saved.has(selected.id) ? openSaved : { label: t("ctaSave", "Save this route"), saves: true, onClick: () => saveRoute(selected) };
	})();

	// What the map draws: my route, the open route, or (the first view) the route in turn
	const mapStops = tab === "mine" ? state.stops : (selected?.stops ?? previewRoute?.stops ?? NO_STOPS);
	// The camera frames the open tab again when a tab or a route opens or is previewed, when a stop is taken out of my route, and when a route is copied into it:
	// not when a stop is added (it goes where the visitor is looking), moved or lengthened.
	const mapKey = `${tab === "mine" ? "mine" : (selected?.id ?? previewRoute?.id ?? "")}:${framing}`;
	const point = place ? (data.points.find((p) => p.slug === place) ?? null) : null;
	const showPointer = overview && !point;

	// The photo behind the header: the route on the map, else the country's
	const route = tab === "routes" ? (selected ?? previewRoute) : null;
	const headPoint = (route && images.get(route.id)) || hero;

	// The pointer card sits level with the list it points at: its top is the list's "Pick a route" heading
	const placePointer = useCallback(() => {
		const map = mapRef.current;
		const anchor = frameRef.current?.querySelector<HTMLElement>("[data-pick-head]");
		if (!map || !anchor) return;
		const top = anchor.getBoundingClientRect().top - map.getBoundingClientRect().top;
		setPointerTop(Math.max(18, Math.min(map.clientHeight - (pointerRef.current?.offsetHeight ?? 110) - 18, Math.round(top))));
	}, []);
	useBrowserLayoutEffect(() => {
		if (!showPointer) return undefined;
		placePointer();
		window.addEventListener("resize", placePointer);
		return () => window.removeEventListener("resize", placePointer);
	}, [showPointer, placePointer, filter, lang, mounted, folded]);

	const onMapLogo = () => track("map_logo");
	const mapLogoHref = phone ? phoneOnelink(onelink, "map_logo") : `/${lang === "en" ? "" : `${lang}/`}`;

	const groupLabel = (g: DurationGroup | "all") =>
		data.groupLabels[g] || (g === "all" ? t("filterAll", "All") : ui(`guide.routeMap.group.${g}`, GROUP_LABEL[g]));
	const eyebrow = t("eyebrow", "{country} · country guide");

	return (
		<>
			<section id={ROUTE_MAP_ID} className={`${discoverStyles.routesSection} ${styles.section}`}>
				<div className={discoverStyles.routesHeader}>
					<div className={discoverStyles.routesHeaderRow}>
						<div>
							<p className={discoverStyles.routesKicker}>{heading.kicker}</p>
							<h2 className={discoverStyles.routesTitle}>{heading.h2}</h2>
							<p className={discoverStyles.routesDesc}>
								{fillUi(
									ui(
										"guide.chrome.routesIntro",
										"{name} is loved by culture seekers and backpackers alike. We recommend several routes through this country, here they are in full!"
									),
									{ name }
								)}
							</p>
						</div>
					</div>
				</div>
				<div className={styles.shell}>
					<div
						ref={frameRef}
						className={`${styles.frame} ${folded ? styles.frameFolded : ""}`}
						onPointerDownCapture={stopAuto}
						onKeyDownCapture={stopAuto}
						onWheelCapture={stopAuto}
					>
						<div className={styles.header}>
							{headPoint?.photo && <HeaderPhoto src={headPoint.photo} />}
							<div className={styles.headRow}>
								<span className={styles.headLogo} aria-hidden="true">
									<img src={stipplLogoWhite} width={113} height={30} alt="" />
								</span>
								<span className={styles.headEyebrow}>{eyebrow}</span>
							</div>
							<p className={styles.headTitle}>{t("title", "Plan your {country} route")}</p>
							<div className={styles.tabs} role="tablist">
								{TABS.map((x) => (
									<button
										key={x}
										type="button"
										role="tab"
										aria-selected={x === tab}
										className={`${styles.tab} ${x === tab ? styles.tabOn : ""}`}
										onClick={() => switchTab(x)}
									>
										{x === "routes" ? t("tabRoutes", "Routes") : t("tabMine", "My route")}
										<small>{tabSub[x]}</small>
									</button>
								))}
							</div>
						</div>
						<div className={styles.panel}>
							{tab === "routes" && selected && (
								<button type="button" className={styles.backTo} onClick={allRoutes}>
									<Back size={16} color="currentColor" weight={2.6} />
									{t("allRoutes", "All routes")}
								</button>
							)}
							{/* the add sheet has its own header, as in the app: the stepper steps aside */}
							{!adding && <Steps step={step} />}
							<div className={styles.body}>
								{tab === "routes" && (
									<RoutesTab
										routes={routes}
										groups={groups}
										filter={filter}
										onFilter={filterBy}
										groupLabel={groupLabel}
										points={data.points}
										images={images}
										countryCode={countryCode}
										placeCount={data.points.length}
										selected={selected}
										preview={previewRoute?.id ?? null}
										onSelect={select}
										onPreview={onPreview}
										onScratch={scratch}
										placeSlug={place}
										onPlace={selectPlace}
										onHover={setHover}
									/>
								)}
								{tab === "mine" && (
									<Suspense fallback={null}>
										<MineTab
											data={data}
											countryCode={countryCode}
											routeId={state.selectedRouteId}
											stops={state.stops}
											startDate={state.startDate}
											flex={state.flex}
											adding={adding}
											onAdding={setAdding}
											onAdd={addPoint}
											onPickRoute={customise}
											onPlace={selectPlace}
											onHover={setHover}
											onReframe={() => setFraming((n) => n + 1)}
										/>
									</Suspense>
								)}
							</div>
							<div className={styles.foot}>
								{save.status === "error" && cta.saves && (
									<p className={styles.error} role="alert">
										{t("saveError", "We couldn't save your route just now. Please try again.")}
									</p>
								)}
								{cta.saved && (
									<p className={styles.saved} role="status">
										{t("savedTitle", "Saved to your Stippl account")}
									</p>
								)}
								<div className={styles.ctaRow}>
									{tab === "routes" && selected && selected.stops.length > 0 && (
										<SecondaryCta label={t("customise", "Customise")} onClick={() => customise(selected)}>
											{t("customise", "Customise")}
										</SecondaryCta>
									)}
									<PanelCta
										dark={cta.dark}
										free={!cta.dark && !cta.saved}
										busy={cta.saves && working}
										href={cta.href}
										onClick={cta.onClick}
										label={cta.saves && working ? t("saving", "Saving…") : cta.label}
									>
										{cta.saves && working ? t("saving", "Saving…") : cta.label}
									</PanelCta>
								</div>
							</div>
						</div>
						<div className={styles.map} ref={mapRef}>
							<MapSlot>
								<RouteMapScene
									label={t("mapAria", "Map of {country}")}
									points={data.points}
									stops={mapStops}
									routeKey={mapKey}
									overview={overview}
									preview={!!previewRoute}
									selected={place}
									hovered={hover}
									sheetRef={sheetRef}
									pointerRef={pointerRef}
									onSelect={selectPlace}
									onHover={setHover}
								/>
							</MapSlot>
							<a className={styles.mapLogo} href={mapLogoHref} onClick={onMapLogo} aria-label={t("openStippl", "Open in Stippl")}>
								<i className={styles.dot} aria-hidden="true" />
								<img src={stipplLogo} width={113} height={30} alt="" aria-hidden="true" />
								<span>{t("openApp", "Open in app")}</span>
							</a>
							<button
								type="button"
								className={styles.fold}
								aria-label={folded ? t("showList", "Show the list") : t("hideList", "Hide the list")}
								aria-expanded={!folded}
								onClick={() => setFolded((f) => !f)}
							>
								<Back size={18} color="currentColor" weight={2.6} />
							</button>
							{showPointer && (
								<div ref={pointerRef} className={styles.pointer} style={pointerTop != null ? { top: pointerTop } : undefined}>
									<span className={styles.pointerArrow} aria-hidden="true">
										<Back size={20} color="currentColor" weight={2.6} />
									</span>
									<div>
										<b className={styles.pointerLeft}>{t("pointerLeft", "Pick a route on the left")}</b>
										<b className={styles.pointerBelow}>{t("pointerBelow", "Pick a route below")}</b>
										<p>{t("pointerSub", "See it on the map, then make it your own.")}</p>
										<button type="button" onClick={scratch}>
											{t("pointerOwn", "or start from scratch")}
										</button>
									</div>
								</div>
							)}
							<div className={styles.toastHost} role="status">
								{toast && (
									<span key={toast.id} className={styles.toast}>
										{toast.text}
									</span>
								)}
							</div>
						</div>
						{/* The card of the open destination: over the map's corner, over the panel's foot on a phone. */}
						{point && (
							<PlaceSheet
								ref={sheetRef}
								point={point}
								countrySlug={countrySlug}
								inRoute={inRoute.has(point.slug)}
								onToggle={() => togglePlace(point)}
								onClose={() => setPlace(null)}
							/>
						)}
					</div>
				</div>
				{/* Modal on a phone too: saving a route is a signup here, not a trip to the store (the hub's other plan CTAs go there). */}
				<PlanGateModal
					gate={save.gate}
					title={ui("guide.routeMap.auth", "Create a free account to save this route")}
					loginTitle={ui("guide.routeMap.login", "Log in to save this route")}
					allowMobile
					onLogin={save.onLogin}
				/>
				{/* "Plan {country} in Stippl" and "Start from scratch": the same modal on its own gate; once in, the visitor goes to the app's home. */}
				<PlanGateModal
					gate={save.scratchGate}
					title={ui("guide.routeMap.authScratch", "Create a free account to plan your trip")}
					loginTitle={ui("guide.routeMap.loginScratch", "Log in to plan your trip")}
					allowMobile
				/>
			</section>
		</>
	);
};

export default CountryRouteMap;
